import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Modal } from "react-bootstrap";
import { getAuditLogs } from "../../../services/AuditLogService";
import DataTablePagination from "../common/DataTablePagination";
import { useTranslation } from "react-i18next";
import { getLocale } from "../../../utils/i18nLabels";

const actionBadge = {
  CREATE: "success",
  UPDATE: "info",
  DELETE: "danger",
  RESTORE: "success",
  LOGIN: "success",
  LOGIN_FAILED: "danger",
  LOGOUT: "secondary",
  REGISTER: "primary",
  PUBLISH: "success",
  ARCHIVE: "warning",
  CANCEL: "warning",
  ASSIGN_ROLE: "info",
  REMOVE_ROLE: "warning",
  SET_PERMISSIONS: "info",
};

const AuditLogs = () => {
  const { t } = useTranslation();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const [actionFilter, setActionFilter] = useState("");
  const [entityTypeFilter, setEntityTypeFilter] = useState("");
  const [viewingDetails, setViewingDetails] = useState(null);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    setLoadError("");
    try {
      const params = { page, limit, sort_by: "created_at", sort_dir: "desc" };
      if (actionFilter) params.action = actionFilter;
      if (entityTypeFilter) params.entity_type = entityTypeFilter;

      const res = await getAuditLogs(params);
      const body = res.data;
      setLogs(body.data || []);
      setTotal(body.pagination?.total ?? 0);
      setTotalPages(body.pagination?.totalPages ?? 1);
    } catch (err) {
      setLoadError(err?.response?.data?.message || t("pages.auditLogs.loadError"));
      setLogs([]);
    } finally {
      setLoading(false);
    }
  }, [page, limit, actionFilter, entityTypeFilter, t]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const resetFilters = () => {
    setActionFilter("");
    setEntityTypeFilter("");
    setPage(1);
  };

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
          <div className="my-auto mb-2">
            <nav>
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">
                  <Link to="/dashboard">{t("menu.dashboard.title")}</Link>
                </li>
                <li className="breadcrumb-item active">{t("menu.auditLogs")}</li>
              </ol>
            </nav>
          </div>
          <div className="mb-2">
            <button type="button" className="btn btn-outline-light bg-white btn-icon" onClick={resetFilters}>
              <i className="ti ti-refresh"></i>
            </button>
          </div>
        </div>

        <div className="card">
          <div className="card-header d-flex align-items-center justify-content-between flex-wrap pb-0">
            <h4 className="mb-3">{t("pages.auditLogs.title")}</h4>
            <div className="d-flex align-items-center flex-wrap">
              <div className="mb-3 me-2">
                <input
                  type="text"
                  className="form-control"
                  placeholder={t("pages.auditLogs.actionTypePlaceholder")}
                  value={actionFilter}
                  onChange={(e) => {
                    setActionFilter(e.target.value);
                    setPage(1);
                  }}
                  style={{ minWidth: "180px" }}
                />
              </div>
              <div className="mb-3 me-2">
                <input
                  type="text"
                  className="form-control"
                  placeholder={t("pages.auditLogs.entityTypePlaceholder")}
                  value={entityTypeFilter}
                  onChange={(e) => {
                    setEntityTypeFilter(e.target.value);
                    setPage(1);
                  }}
                  style={{ minWidth: "180px" }}
                />
              </div>
            </div>
          </div>

          <DataTablePagination
            variant="header"
            page={page}
            limit={limit}
            total={total}
            totalPages={totalPages}
            onPageChange={setPage}
            onLimitChange={(v) => {
              setLimit(v);
              setPage(1);
            }}
          />

          <div className="card-body p-0 py-3">
            <div className="custom-datatable-filter table-responsive">
              <table className="table datatable">
                <thead className="thead-light">
                  <tr>
                    <th>{t("common.ui.date")}</th>
                    <th>{t("header.defaultUser")}</th>
                    <th>{t("common.crud.actionColumn")}</th>
                    <th>{t("common.ui.entite")}</th>
                    <th>{t("pages.auditLogs.ipAddressColumn")}</th>
                    <th>{t("pages.auditLogs.detailsColumn")}</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan="6" className="text-center py-4">
                        <div className="spinner-border text-primary" role="status">
                          <span className="visually-hidden">{t("common.crud.loading")}</span>
                        </div>
                      </td>
                    </tr>
                  ) : loadError ? (
                    <tr>
                      <td colSpan="6" className="text-center py-4 text-danger">
                        {loadError}
                      </td>
                    </tr>
                  ) : logs.length > 0 ? (
                    logs.map((log) => (
                      <tr key={log.id}>
                        <td>{new Date(log.created_at).toLocaleString(getLocale())}</td>
                        <td>{log.user_email || <span className="text-muted">{t("common.notificationType.system")}</span>}</td>
                        <td>
                          <span className={`badge badge-soft-${actionBadge[log.action] || "secondary"}`}>{log.action}</span>
                        </td>
                        <td>
                          {log.entity_type}
                          {log.entity_id && <span className="text-muted small d-block">{log.entity_id}</span>}
                        </td>
                        <td>{log.ip_address || "-"}</td>
                        <td>
                          {log.details ? (
                            <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setViewingDetails(log.details)}>
                              {t("pages.auditLogs.view")}
                            </button>
                          ) : (
                            "-"
                          )}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="6" className="text-center py-4">
                        <i className="ti ti-search-off fs-1 text-muted"></i>
                        <h5 className="mt-2">{t("pages.auditLogs.noEntriesFound")}</h5>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <DataTablePagination
              variant="footer"
              page={page}
              limit={limit}
              total={total}
              totalPages={totalPages}
              onPageChange={setPage}
              onLimitChange={(v) => {
                setLimit(v);
                setPage(1);
              }}
            />
          </div>
        </div>
      </div>

      <Modal show={viewingDetails !== null} onHide={() => setViewingDetails(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>{t("pages.auditLogs.actionDetailsTitle")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <pre className="mb-0" style={{ whiteSpace: "pre-wrap" }}>
            {JSON.stringify(viewingDetails, null, 2)}
          </pre>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default AuditLogs;
